import { UserManager } from 'oidc-client-ts';
import type { ReactNode } from 'react';
import { type Root } from 'react-dom/client';
import { Button, Spinner } from 'ui';
import { setUserManager } from './userManager.ts';

interface BootstrapAuthAppOptions {
  root: Root;
  authority: string;
  selectorUrl?: string;
  createUserManager: () => UserManager;
  renderApp: (userManager: UserManager) => ReactNode;
}

const renderLoading = (root: Root) => {
  root.render(
    <div className="flex h-[100dvh] w-full items-center justify-center">
      <Spinner size="lg" />
    </div>,
  );
};

const renderAuthUnavailable = (root: Root, selectorUrl: string, onRetry: () => void) => {
  root.render(
    <div className="flex h-[100dvh] w-full items-center justify-center bg-white px-4">
      <div className="flex w-full max-w-lg flex-col items-center space-y-5 rounded-xl border border-gray-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-2xl font-semibold text-gray-900">Authentication service unavailable</h1>
        <p className="text-sm leading-6 text-gray-600">
          The sign-in service cannot be reached right now. Please try again later or open the app
          selector.
        </p>
        <div className="flex w-full flex-col items-center gap-2 sm:flex-row justify-center sm:gap-3">
          <Button
            onClick={() => {
              window.location.href = selectorUrl || window.location.origin;
            }}
            className="w-full px-3 sm:w-auto"
          >
            Go to app selector
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              void onRetry();
            }}
            className="w-full px-3 sm:w-auto"
          >
            Retry
          </Button>
        </div>
      </div>
    </div>,
  );
};

const isKeycloakReachable = async (authority: string) => {
  if (!authority) return false;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(`${authority}/.well-known/openid-configuration`, {
      signal: controller.signal,
      cache: 'no-store',
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

const isKeycloakReachableWithRetry = async (authority: string) => {
  const firstAttempt = await isKeycloakReachable(authority);
  if (firstAttempt) {
    return true;
  }

  await sleep(5000);
  return isKeycloakReachable(authority);
};

export const bootstrapAuthApp = async ({
  root,
  authority,
  selectorUrl,
  createUserManager,
  renderApp,
}: BootstrapAuthAppOptions) => {
  const bootstrap = async () => {
    renderLoading(root);

    if (!(await isKeycloakReachableWithRetry(authority))) {
      renderAuthUnavailable(root, selectorUrl || window.location.origin, bootstrap);
      return;
    }

    const userManager = createUserManager();
    setUserManager(userManager);
    root.render(renderApp(userManager));
  };

  await bootstrap();
};
